import { useEffect, useRef, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Loader2, RotateCcw, Send, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAssistantChat } from './useAssistantChat';
import { useAssistantUsage } from './api';
import { ActionCard } from './ActionCard';

export function AssistantPanel({
  workspaceId,
  open,
  onOpenChange,
}: {
  workspaceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation('assistant');
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  const errorText = { generic: t('error.generic'), refusal: t('error.refusal'), limit: t('error.limit') };
  const { transcript, busy, toolNote, usage: liveUsage, send, stop, reset } = useAssistantChat({ workspaceId, errorText });
  const { data: fetchedUsage } = useAssistantUsage(workspaceId, open);
  // Prefer the live usage from the last send over the query data so the count
  // updates immediately after a message, without waiting on a refetch.
  const usage = liveUsage ?? fetchedUsage ?? null;
  const limitReached = usage?.remaining === 0;

  // Abort any in-flight stream as soon as the panel is closed, per the spec.
  useEffect(() => {
    if (!open) stop();
  }, [open, stop]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [transcript, toolNote]);

  const sendDisabled = busy || !input.trim() || limitReached;

  const handleSend = () => {
    if (sendDisabled) return;
    void send(input);
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleOpenLink = (deepLink: string) => {
    onOpenChange(false);
    navigate(deepLink);
  };

  const examples = [t('examples.one'), t('examples.two')];

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <DialogPrimitive.Content className="fixed inset-x-0 bottom-0 z-50 flex max-h-[90vh] flex-col border-t border-border bg-surface pb-safe shadow-lg md:inset-x-auto md:right-0 md:top-0 md:h-screen md:max-h-none md:w-full md:max-w-md md:border-l md:border-t-0 md:pb-0">
          <div className="border-b border-border px-4 py-3">
            <div className="flex items-center justify-between">
              <DialogPrimitive.Title className="font-display text-base font-bold text-ink">{t('title')}</DialogPrimitive.Title>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" aria-label={t('clear')} onClick={reset}>
                  <RotateCcw size={16} />
                </Button>
                <DialogPrimitive.Close className="p-1 text-muted hover:bg-surface-2" aria-label={t('close')}>
                  <X size={16} />
                </DialogPrimitive.Close>
              </div>
            </div>
            <p className="mt-1 text-xs text-muted">{t('disclaimer')}</p>
            {usage ? (
              <p className="text-xs text-muted">
                {limitReached ? t('error.limit') : t('messagesLeft', { count: usage.remaining, limit: usage.limit })}
              </p>
            ) : null}
          </div>

          <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
            {transcript.length === 0 ? (
              <div className="space-y-3 p-4">
                <p className="text-sm text-muted">{t('welcome')}</p>
                <div className="flex flex-col gap-2">
                  {examples.map((example) => (
                    <button
                      key={example}
                      type="button"
                      onClick={() => setInput(example)}
                      className="rounded-[var(--radius-input)] border border-border bg-surface-2 px-3 py-2 text-left text-xs text-ink hover:bg-border"
                    >
                      {example}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-3 p-4">
                {transcript.map((entry, i) =>
                  entry.role === 'user' ? (
                    <div key={i} className="flex justify-end">
                      <div className="max-w-[85%] bg-surface-2 px-3 py-2 text-sm text-ink">{entry.content}</div>
                    </div>
                  ) : (
                    <div key={i}>
                      <p className={`whitespace-pre-wrap text-sm ${entry.status === 'error' ? 'text-danger' : 'text-ink'}`}>
                        {entry.content}
                      </p>
                      {entry.status === 'error' ? <p className="mt-1 text-xs text-muted">{t('error.retryHint')}</p> : null}
                      {entry.actions?.map((action) => (
                        <ActionCard key={action.id} action={action} workspaceId={workspaceId} onOpenLink={handleOpenLink} />
                      ))}
                    </div>
                  ),
                )}
                {toolNote ? (
                  <div className="flex items-center gap-2 text-xs text-muted">
                    <Loader2 size={12} className="animate-spin" />
                    {t('working')}
                  </div>
                ) : null}
              </div>
            )}
          </div>

          <div className="border-t border-border p-3">
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('placeholder')}
                rows={1}
                disabled={limitReached}
                className="min-h-10 flex-1 resize-none rounded-[var(--radius-input)] border border-border bg-surface px-3 py-2 text-sm outline-none focus-visible:border-accent"
              />
              <Button variant="primary" size="icon" aria-label={t('send')} disabled={sendDisabled} onClick={handleSend}>
                <Send size={16} />
              </Button>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
